import type React from 'react';
import { BuildCommit } from '@/components/buildCommit';
import { ModeBadge } from '@/components/modeBadge';
import { getNodeTestProps } from '@/utils';
import { DashboardLanguageToggle } from '@/layout/components/DashboardLanguageToggle';
import { DashboardThemeToggle } from '@/layout/components/DashboardThemeToggle';
import { SellerRosterStep } from './components/SellerRosterStep';
import { SellerPinStep } from './components/SellerPinStep';
import { SellerLoginShortcutsHint } from './components/SellerLoginShortcutsHint';
import type { SellerLoginLogic } from './useSellerLogin';

/**
 * Seller sign-in screen (render-only). A full-screen kiosk frame that swaps
 * between the roster step and the PIN step depending on whether a seller has
 * been chosen. All state + handlers come from `useSellerLogin`. The `F1` hint
 * and the build commit sit below either step.
 */
export const SellerLoginView: React.FC<SellerLoginLogic> = ({
    branchName,
    selectedUser,
    focusUserId,
    rosterUsers,
    rosterLoading,
    rosterError,
    rosterErrorKey,
    signOutNoticeKey,
    pin,
    pinErrorMessage,
    isPinHeld,
    isSubmitting,
    selectUser,
    back,
    pressDigit,
    pressBackspace,
    pressEscape,
    reEnroll,
}) => (
    <div
        className="flex min-h-screen flex-col items-center justify-center bg-background px-4 py-10 text-foreground"
        {...getNodeTestProps('seller-login-screen')}
    >
        <div className="absolute right-4 top-4 flex items-center gap-2">
            <DashboardLanguageToggle
                className="gap-0"
                id="seller-login-language-toggle"
                itemIdPrefix="seller-login"
            />
            <DashboardThemeToggle
                className="gap-0"
                id="seller-login-theme-toggle"
                itemIdPrefix="seller-login"
            />
        </div>

        {selectedUser ? (
            <SellerPinStep
                isSubmitting={isSubmitting}
                onBack={back}
                onBackspace={pressBackspace}
                onDigit={pressDigit}
                onEscape={pressEscape}
                pin={pin}
                isPinHeld={isPinHeld}
                pinErrorMessage={pinErrorMessage}
                user={selectedUser}
            />
        ) : (
            <SellerRosterStep
                branchName={branchName}
                errorKey={rosterErrorKey}
                focusUserId={focusUserId}
                isError={rosterError}
                isLoading={rosterLoading}
                onReEnroll={reEnroll}
                onSelect={selectUser}
                signOutNoticeKey={signOutNoticeKey}
                users={rosterUsers}
            />
        )}

        <div className="mt-6 flex flex-col items-center gap-2">
            <SellerLoginShortcutsHint />
            <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
                <BuildCommit />
                <ModeBadge />
            </div>
        </div>
    </div>
);
